import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from '@ami/react/core';
import { AmiDateRangePicker } from '@ami/react/core';
import { appConfig } from '@ami/react/core';
import { Input } from 'reactstrap';
import { OrderFilter } from '../../filterConfiguration';
import { FilterForm, FilterInputRow, FilterSearchRow } from '@ami/react/core';
import { CountryAndRegionSelection } from '../../../geo-locations/CountryAndRegionSelection';
import { DealerGroupsAsyncDropdown } from '../../../dealer-groups/DealerGroupsAsyncDropdown';
import { DealerBranchesAsyncDropdown } from '../../../dealer-branches/DealerBranchesAsyncDropdown';
import { DealerUsersAsyncDropdown } from '../../../dealer-users/DealerUsersAsyncDropdown';
import MachineMakeAndModelFilterComponent from '../../../machines/MachineMakeAndModelFilterComponent';
import { User } from '../../../users/user';
import Item from '../../../Item';
import { IndustriesAsyncDropdown } from '../../../industries/IndustriesAsyncDropdown';

const CommonFilters = ({ statuses, filter, updateFilter }) => {
  return (
    <React.Fragment>
      <FilterInputRow label="Search" labelClassName="col-form-label">
        <Input
          placeholder="Search by name, order #, PO #, quote #"
          value={filter.search || ''}
          onChange={(e) => updateFilter({ search: e.target.value })}
        />
      </FilterInputRow>
      <FilterInputRow label="Part Number" labelClassName="col-form-label">
        <Input
          placeholder="Search by part number"
          value={filter.partNumber || ''}
          onChange={(e) => updateFilter({ partNumber: e.target.value })}
        />
      </FilterInputRow>
      <FilterInputRow label="Serial Number" labelClassName="col-form-label">
        <Input
          placeholder="Search by serial number"
          value={filter.serialNumber || ''}
          onChange={(e) => updateFilter({ serialNumber: e.target.value })}
        />
      </FilterInputRow>
      <FilterInputRow label="Stock Number" labelClassName="col-form-label">
        <Input
          placeholder="Searcy by stock number"
          value={filter.dealerItemId || ''}
          onChange={(e) => updateFilter({ dealerItemId: e.target.value })}
        />
      </FilterInputRow>
      <FilterInputRow label="Status" labelClassName="col-form-label">
        <Dropdown
          placeholder="All"
          isClearable={true}
          items={statuses}
          selectedId={filter.status}
          onChange={(status) => updateFilter({ status })}
          className="text-start"
        />
      </FilterInputRow>
      <FilterInputRow label="Order Date" labelClassName="col-form-label">
        <AmiDateRangePicker
          startDate={filter.startDate}
          endDate={filter.endDate}
          onStartDateChange={(startDate) => updateFilter({ startDate })}
          onEndDateChange={(endDate) => updateFilter({ endDate })}
        />
      </FilterInputRow>
      <FilterInputRow label="Ship Date" labelClassName="col-form-label">
        <AmiDateRangePicker
          startDate={filter.shipDateStartDate}
          endDate={filter.shipDateEndDate}
          onStartDateChange={(shipDateStartDate) => updateFilter({ shipDateStartDate })}
          onEndDateChange={(shipDateEndDate) => updateFilter({ shipDateEndDate })}
        />
      </FilterInputRow>
      <MachineMakeAndModelFilterComponent
        filter={filter}
        updateFilter={updateFilter}
      />
    </React.Fragment>
  );
};

const ManagementOnlyFilters = ({ amiUsers, filter, updateFilter }) => {
  return (
    <React.Fragment>
      <FilterInputRow label="Shipping Location" labelClassName="col-form-label">
        <CountryAndRegionSelection
          isSearchMode={true}
          allowRegionSelection={true}
          selectedCountryCode={filter.shippingLocationCountry}
          selectedRegionCode={filter.shippingLocationRegion}
          onCountrySelected={(shippingLocationCountry) =>
            updateFilter({ shippingLocationCountry })
          }
          onRegionSelected={(shippingLocationRegion) =>
            updateFilter({ shippingLocationRegion })
          }
        />
      </FilterInputRow>
      <FilterInputRow label="Sales Person / TM" labelClassName="col-form-label">
        <Dropdown
          placeholder="All"
          isClearable={true}
          forceSearchable={true}
          items={amiUsers}
          selectedId={filter.contactAmiUserId}
          onChange={(contactAmiUserId) =>
            updateFilter({
              contactAmiUserId,
            })
          }
          className="text-start"
        />
      </FilterInputRow>

      <FilterInputRow label="Dealer Group" labelClassName="col-form-label">
        <DealerGroupsAsyncDropdown
          placeholder="All"
          onChange={(group) =>
            updateFilter({
              dealerGroupId: group ? group.id : null,
              dealerGroupName: group ? group.label : null,
              dealerBranchId: null,
              dealerContactId: null,
            })
          }
          selected={
            filter.dealerGroupId
              ? {
                id: filter.dealerGroupId,
                label: filter.dealerGroupName,
              }
              : null
          }
        />
      </FilterInputRow>
      <FilterInputRow label="Dealer Branch" labelClassName="col-form-label">
        <DealerBranchesAsyncDropdown
          dealerGroupId={filter.dealerGroupId}
          onChange={(branch) =>
            updateFilter({
              dealerBranchId: branch ? branch.id : null,
              dealerBranchName: branch ? branch.label : null,
            })
          }
          selected={
            filter.dealerBranchId
              ? {
                id: filter.dealerBranchId,
                label: filter.dealerBranchName,
              }
              : null
          }
        />
      </FilterInputRow>
    </React.Fragment>
  );
};

const DealerUsersFilters = ({ filter, updateFilter }) => {
  return (
    <React.Fragment>
      <FilterInputRow label="Dealer Contact" labelClassName="col-form-label">
        <DealerUsersAsyncDropdown
          dealerGroupId={filter.dealerGroupId}
          onChange={(user) =>
            updateFilter({
              dealerContactId: user ? user.id : null,
              dealerContactName: user ? user.label : null,
            })
          }
          selected={
            filter.dealerContactId
              ? {
                id: filter.dealerContactId,
                label: filter.dealerContactName,
              }
              : null
          }
        />
      </FilterInputRow>
    </React.Fragment>
  );
};

const IndustryFilter = ({ filter, updateFilter }) => {
  return (
    <React.Fragment>
      <FilterInputRow label="Industry" labelClassName="col-form-label">
        <IndustriesAsyncDropdown
          dealerGroupId={filter.industryId}
          onChange={(industry) =>
            updateFilter({
              industryId: industry ? industry.id : null,
              industryName: industry ? industry.label : null,
            })
          }
          selected={
            filter.industryId
              ? {
                id: filter.industryId,
                label: filter.industryName,
              }
              : null
          }
          addNotSpecifiedOption={true}
        />
      </FilterInputRow>
    </React.Fragment>
  )
}

export default class OrderFilterComponent extends React.Component {
  static propTypes = {
    filter: PropTypes.instanceOf(OrderFilter).isRequired,
    statuses: PropTypes.arrayOf(PropTypes.instanceOf(Item)).isRequired,
    amiUsers: PropTypes.arrayOf(PropTypes.instanceOf(User)).isRequired,
    onChange: PropTypes.func.isRequired,
    onFilter: PropTypes.func.isRequired,
  };

  updateFilter = (filter) =>
    this.props.onChange({ ...this.props.filter, ...filter });

  render() {
    const { filter, statuses, amiUsers } = this.props;

    return (
      <FilterForm onSearch={() => this.props.onFilter()}>
        <CommonFilters
          statuses={statuses}
          filter={filter}
          updateFilter={this.updateFilter}
        />
        {appConfig.isManagementApp ? (
          <ManagementOnlyFilters
            amiUsers={amiUsers}
            filter={filter}
            updateFilter={this.updateFilter}
          />
        ) : null}
        <DealerUsersFilters filter={filter} updateFilter={this.updateFilter} />

        {appConfig.isManagementApp ? (
          <IndustryFilter
            filter={filter}
            updateFilter={this.updateFilter}
          />
        ) : null}
        <FilterSearchRow />
      </FilterForm>
    );
  }
}
